import { getPosts } from '../features/post';
import { getPublications } from '../features/publication';
import { getUsers } from '../features/user';
import type { PageContext } from '../lib/interfaces/page';

export interface SitemapProps {}

function Sitemap(): void {
    return undefined;
}

const userQuery = {
    filter: {
        onboarding: { $not: 0 }, // zero is equivalent to null in this case
    },
    sort: 'id:asc',
};

const publicationQuery = {
    filter: {
        state: 'published',
        tmp_is_paid: { $not: true },
    },
    pagination: {
        limit: 100000,
        start: 0,
    },
};

const postQuery = {
    filters: {
        publishedAt: { $not: 0 }, // zero is equivalent to null in this case
        publication: {
            state: 'published',
            tmp_is_paid: { $not: true },
        },
    },
    populate: {
        publication: {
            fields: { 0: 'slug' },
        },
    },
    pagination: {
        limit: 100000,
        start: 0,
    },
};

interface Path {
    url: string;
    lastmod?: string;
}

export async function getServerSideProps(context: PageContext): Promise<{ props: SitemapProps }> {
    const APP_URL = process.env.NEXT_PUBLIC_APP_URL;

    const paths: Path[] = [];
    paths.push({ url: `${APP_URL}/` });
    paths.push({ url: `${APP_URL}/register` });
    paths.push({ url: `${APP_URL}/remind-password` });
    paths.push({ url: `${APP_URL}/magic-link` });
    paths.push({ url: `${APP_URL}/login` });

    try {
        const users = await getUsers(userQuery);
        const { data: publications } = await getPublications(publicationQuery);
        /**
         * @todo Use the pagination of posts to get them all
         */
        const posts = await getPosts(postQuery);

        const publicationUrls = publications.map((publication) => ({
            url: `${APP_URL}/publication/${publication.slug}`,
            lastmod: publication.updatedAt,
        }));

        const userUrls = users
            .filter(
                (user) =>
                    user.slug !== '' &&
                    user.slug !== null &&
                    user.slug !== user.id.toString() &&
                    Number.isNaN(parseInt(user.slug, 10)),
            )
            .map((user) => ({ url: `${APP_URL}/user/${user.slug}`, lastmod: user.updatedAt }));

        const postUrls = posts.data
            .filter((post) => typeof post.publication === 'object')
            .map((post) => ({
                url: `${APP_URL}/publication/${post.publication?.slug}/post/${post.id}`,
                lastmod: post.updatedAt,
            }));

        paths.push(...userUrls, ...publicationUrls, ...postUrls);

        const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
        <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
        ${paths
            .map(
                (path) => `
                <url>
                <loc>${path.url}</loc>
                ${
                    typeof path.lastmod !== 'undefined'
                        ? `<lastmod>${new Date(path.lastmod).toISOString()}</lastmod>`
                        : ''
                }
                </url>
            `,
            )
            .join('')}
        </urlset>
    `;

        context.res.setHeader('Content-Type', 'text/xml');
        context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=3600');
        context.res.write(sitemap);
        context.res.end();
    } catch (error) {
        console.error(error);
    }

    return {
        props: {},
    };
}

export default Sitemap;
